import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Cable, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useMcpOauthConnections } from '../hooks/useMcpOauthConnections'
import { useRevokeMcpOauthConnection } from '../hooks/useRevokeMcpOauthConnection'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useDateTimeFormatters } from '@/lib/i18n/useDateTimeFormatters'

/**
 * Companion to {@link PersonalAccessTokensCard} for spec-compliant MCP OAuth
 * sessions. Read + revoke only — connections are minted by the bridge AS
 * when a user logs in via an MCP client; they cannot be created from this
 * UI.
 *
 * Scopes are rendered as a compact list using only the trailing path
 * segment (`read` / `write` / `admin`) rather than the full Cognito
 * Resource Server identifier, which would dominate the table width.
 */
export function McpOauthConnectionsCard() {
  const { t } = useTranslation('settings')
  const { smartTime } = useDateTimeFormatters()
  const connectionsQuery = useMcpOauthConnections()
  const revoke = useRevokeMcpOauthConnection()

  const [revokeTarget, setRevokeTarget] = useState<{
    id: string
    name: string
  } | null>(null)

  const connections = connectionsQuery.data ?? []

  const handleConfirmRevoke = async () => {
    if (!revokeTarget) return
    try {
      await revoke.mutateAsync(revokeTarget.id)
      setRevokeTarget(null)
    } catch {
      toast.error(t('integrationsPage.oauthConnections.revokeFailed'))
    }
  }

  const shortScope = (scope: string): string => {
    // "https://host/mcp/read" → "read". Anything not matching the bridge
    // shape (openid, email, profile, custom) shows verbatim.
    const lastSlash = scope.lastIndexOf('/')
    return lastSlash >= 0 && scope.includes('/mcp/')
      ? scope.slice(lastSlash + 1)
      : scope
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <Cable className="size-4 text-muted-foreground" />
              <CardTitle>
                {t('integrationsPage.oauthConnections.title')}
              </CardTitle>
            </div>
            <CardDescription>
              {t('integrationsPage.oauthConnections.description')}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {connectionsQuery.isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          </div>
        ) : connectionsQuery.isError ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <p className="text-sm text-muted-foreground">
              {t('integrationsPage.oauthConnections.loadFailed')}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => connectionsQuery.refetch()}
            >
              {t('integrationsPage.oauthConnections.retry')}
            </Button>
          </div>
        ) : connections.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {t('integrationsPage.oauthConnections.empty')}
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  {t('integrationsPage.oauthConnections.columnClient')}
                </TableHead>
                <TableHead>
                  {t('integrationsPage.oauthConnections.columnScopes')}
                </TableHead>
                <TableHead>
                  {t('integrationsPage.oauthConnections.columnConnected')}
                </TableHead>
                <TableHead>
                  {t('integrationsPage.oauthConnections.columnLastActive')}
                </TableHead>
                <TableHead className="w-px" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {connections.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="font-medium">
                    {row.clientName}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {row.scopes
                      .map(shortScope)
                      .filter((s) => s !== 'openid')
                      .join(', ')}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {smartTime(new Date(row.createdOn))}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {smartTime(new Date(row.lastRefreshedAt))}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() =>
                        setRevokeTarget({ id: row.id, name: row.clientName })
                      }
                    >
                      {t('integrationsPage.oauthConnections.revoke')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <AlertDialog
        open={revokeTarget !== null}
        onOpenChange={(next) => {
          if (!next && !revoke.isPending) setRevokeTarget(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('integrationsPage.oauthConnections.revokeConfirmTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {revokeTarget?.name ? `"${revokeTarget.name}" — ` : ''}
              {t('integrationsPage.oauthConnections.revokeConfirmBody')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoke.isPending}>
              {t('integrationsPage.oauthConnections.revokeCancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                handleConfirmRevoke()
              }}
              disabled={revoke.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {revoke.isPending && (
                <Loader2 className="size-4 animate-spin mr-2" />
              )}
              {t('integrationsPage.oauthConnections.revokeConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}
